import { useEffect, useState, useRef } from "react";
import {
  Modal,
  Input,
  Button,
  Typography,
  Row,
  Col,
  Collapse,
  Checkbox,
  message,
} from "antd";
import { CheckOutlined } from "@ant-design/icons";
import apiClient from "../../services/apiClient";

const { Title, Text } = Typography;
const { Panel } = Collapse;

const RoleModal = ({
  visible,
  onCancel,
  onSubmit,
  initialValues = {},
  loading = false,
  mode = "add",
  availablePolicies: propPolicies, // new prop
}) => {
  const [name, setName] = useState(initialValues.name || "");
  const [description, setDescription] = useState(
    initialValues.description || ""
  );
  const [availablePolicies, setAvailablePolicies] = useState(
    propPolicies || null
  );
  const [selectedPolicyIds, setSelectedPolicyIds] = useState(
    initialValues.policyIds || []
  );
  const [policiesLoading, setPoliciesLoading] = useState(false);
  const prevVisible = useRef(false);

  // Debug: Log propPolicies when component receives it
  useEffect(() => {
    console.log("propPolicies received:", propPolicies);
  }, [propPolicies]);

  // Debug: Log when availablePolicies changes
  useEffect(() => {
    console.log("availablePolicies state changed:", availablePolicies);
  }, [availablePolicies]);

  // Only reset state when modal is first opened
  useEffect(() => {
    if (visible && !prevVisible.current) {
      setName(initialValues.name || "");
      setDescription(initialValues.description || "");
      setSelectedPolicyIds(initialValues.policyIds || []);
      console.log("Modal opened, propPolicies:", propPolicies);
      if (!propPolicies) {
        console.log("No propPolicies, fetching policies...");
        setPoliciesLoading(true);
        const fetchPolicies = async () => {
          try {
            const baseUrl = process.env.REACT_APP_AUTH_URL || "";
            const res = await apiClient.get(`${baseUrl}/policies`, {
              headers: {
                "ngrok-skip-browser-warning": "application/json",
              },
            });
            
            // Check if response has the expected structure with success field
            if (!res.data.success) {
              throw new Error("Failed to fetch policies - success field is false");
            }
            
            console.log("Policies API response:", res.data.data);
            setAvailablePolicies(res.data.data);
          } catch (e) {
            setAvailablePolicies(null);
            message.error(
              "Failed to fetch policies: " +
                (e.response?.data?.message || e.message)
            );
          } finally {
            setPoliciesLoading(false);
          }
        };
        fetchPolicies();
      } else {
        console.log("Using propPolicies:", propPolicies);
        setAvailablePolicies(propPolicies);
      }
    }
    prevVisible.current = visible;
    // eslint-disable-next-line
  }, [visible]);

  // Helper to get all policy IDs for a group
  const getGroupPolicyIds = (group) => {
    if (!availablePolicies || !availablePolicies[group]) {
      return [];
    }
    
    // Ensure the group data is an array
    const groupPolicies = availablePolicies[group];
    if (!Array.isArray(groupPolicies)) {
      console.error(`Group ${group} is not an array:`, groupPolicies);
      return [];
    }
    
    return groupPolicies.map((policy) => policy.id);
  };

  // Handler to merge checked values from all groups
  const handlePolicyGroupChange = (group, checked) => {
    let allChecked = new Set(selectedPolicyIds);
    // Remove all current group's policy IDs
    getGroupPolicyIds(group).forEach((id) => allChecked.delete(id));
    // Add the new checked values for this group
    checked.forEach((id) => allChecked.add(id));
    setSelectedPolicyIds(Array.from(allChecked));
  };

  const handleOk = () => {
    if (!name.trim() || !description.trim()) {
      message.error("Role name and description are required");
      return;
    }
    onSubmit({
      name: name.trim(),
      description: description.trim(),
      policyIds: selectedPolicyIds,
    });
  };

  return (
    <Modal
      title={
        <span style={{ fontWeight: 600 }}>
          {mode === "edit" ? "Edit Role" : "Create Custom Role"}
        </span>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={900}
      className="custom-role-modal"
      bodyStyle={{ padding: 0 }}
      destroyOnClose
    >
      <Row style={{ minHeight: 500 }}>
        <Col
          xs={24}
          md={10}
          style={{ borderRight: "1px solid #f0f0f0", padding: 32 }}
        >
          <Title level={5}>Role Name</Title>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter role name"
            style={{ marginBottom: 16 }}
          />
          <Title level={5}>Description</Title>
          <Input.TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter role description"
            rows={3}
            style={{ marginBottom: 16 }}
          />
          <Button
            type="primary"
            icon={<CheckOutlined />}
            loading={loading}
            onClick={handleOk}
            style={{ width: 160, marginTop: 16 }}
          >
            {mode === "edit" ? "Save Changes" : "Create Role"}
          </Button>
        </Col>
        <Col xs={24} md={14} style={{ padding: 32 }}>
          <Title level={5}>Assign Permissions</Title>
          {policiesLoading ? (
            <div>Loading policies...</div>
          ) : availablePolicies ? (
            <>
              {console.log("Available policies in render:", availablePolicies)}
              <Collapse defaultActiveKey={Object.keys(availablePolicies)}>
                {Object.keys(availablePolicies).map((group) => (
                  <Panel header={group} key={group}>
                    <Checkbox.Group
                      value={getGroupPolicyIds(group).filter((id) =>
                        selectedPolicyIds.includes(id)
                      )}
                      onChange={(checked) =>
                        handlePolicyGroupChange(group, checked)
                      }
                    >
                      <Row gutter={[0, 8]}>
                        {(() => {
                          const groupPolicies = availablePolicies[group];
                          console.log(`Group ${group}:`, groupPolicies, 'Type:', typeof groupPolicies, 'IsArray:', Array.isArray(groupPolicies));
                          if (Array.isArray(groupPolicies)) {
                            return groupPolicies.map((policy) => (
                              <Col span={24} key={policy.id}>
                                <Checkbox value={policy.id}>
                                  {policy.description}
                                </Checkbox>
                              </Col>
                            ));
                          } else {
                            return (
                              <Col span={24}>
                                <Text type="secondary">No policies available for this group. (Type: {typeof groupPolicies})</Text>
                              </Col>
                            );
                          }
                        })()}
                      </Row>
                    </Checkbox.Group>
                  </Panel>
                ))}
              </Collapse>
            </>
          ) : (
            <Text type="danger">No policies available.</Text>
          )}
        </Col>
      </Row>
    </Modal>
  );
};

export default RoleModal;
